import React from 'react';
import { ArrowLeft, Trash2, Mail, Smartphone } from 'lucide-react';

const Para = ({ children }) => (
  <p className="text-text-main text-sm leading-relaxed mb-3">{children}</p>
);

const DeleteDataPage = ({ onNavigate }) => {
  return (
    <div className="min-h-screen bg-background">
      {/* Brand gradient strip */}
      <div className="h-1 w-full" style={{ background: 'linear-gradient(to right, #4AADA8, #A99CC8)' }} aria-hidden="true" />

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Back button */}
        {onNavigate && (
          <button
            onClick={() => onNavigate('feed')}
            aria-label="Back to Sirati"
            className="flex items-center gap-2 text-sm text-text-muted hover:text-primary transition-colors mb-10 min-h-[44px]"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to Sirati
          </button>
        )}

        {/* Header */}
        <header className="text-center mb-12">
          <p className="font-heading text-3xl font-bold tracking-wide text-primary mb-1">SIRATI</p>
          <h1 className="font-heading text-2xl font-semibold mb-2" style={{ color: '#A99CC8' }}>Data Deletion Request</h1>
          <p className="text-xs text-text-muted">Your data belongs to you. Here is how to remove it.</p>
        </header>

        {/* Divider */}
        <div className="h-px mb-10" style={{ background: 'linear-gradient(to right, transparent, #4AADA820, #A99CC830, #4AADA820, transparent)' }} aria-hidden="true" />

        <Para>
          You have the right to request deletion of your personal data at any time. There are two ways to do this — choose whichever is easiest for you.
        </Para>

        {/* Option 1 — In-app */}
        <section className="mb-8" aria-labelledby="option-inapp">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(74,173,168,0.12)' }}>
              <Smartphone className="w-4 h-4 text-primary" aria-hidden="true" />
            </div>
            <h2 id="option-inapp" className="font-heading text-lg font-semibold text-primary">Option 1 — Delete your account in the app</h2>
          </div>
          <Para>
            The fastest way to remove your account and personal information is directly inside the app:
          </Para>
          <ol className="space-y-2 ml-1 mb-3">
            {[
              'Open Sirati and sign in',
              'Tap your profile icon or go to Profile',
              'Tap Edit Profile',
              'Scroll to the bottom and tap Delete Account',
              'Confirm the deletion',
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-text-main text-sm leading-relaxed">
                <span
                  className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary-foreground"
                  style={{ background: '#4AADA8' }}
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <Para>
            Your profile and personal information will be permanently deleted within 30 days.
          </Para>
        </section>

        {/* Divider */}
        <div className="h-px mb-8" style={{ background: 'linear-gradient(to right, transparent, #B8E0E040, transparent)' }} aria-hidden="true" />

        {/* Option 2 — Email */}
        <section className="mb-10" aria-labelledby="option-email">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(169,156,200,0.12)' }}>
              <Mail className="w-4 h-4 text-secondary" aria-hidden="true" />
            </div>
            <h2 id="option-email" className="font-heading text-lg font-semibold text-primary">Option 2 — Email us a deletion request</h2>
          </div>
          <Para>
            If you no longer have access to your account or prefer to make a written request, email us at:
          </Para>
          <div className="my-5 p-4 rounded-arch-lg border" style={{ borderColor: '#B8E0E0', background: 'rgba(74,173,168,0.05)' }}>
            <a
              href="mailto:salam@sirati.co?subject=Data%20Deletion%20Request"
              className="font-heading font-semibold text-primary hover:underline text-lg"
            >
              salam@sirati.co
            </a>
            <p className="text-xs text-text-muted mt-1">Subject: Data Deletion Request</p>
          </div>
          <Para>
            Please include the email address associated with your Sirati account so we can locate and remove your data. We will confirm receipt and complete the deletion within 30 days.
          </Para>
        </section>

        {/* What gets deleted */}
        <section className="mb-10" aria-labelledby="what-deleted">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(74,173,168,0.12)' }}>
              <Trash2 className="w-4 h-4 text-primary" aria-hidden="true" />
            </div>
            <h2 id="what-deleted" className="font-heading text-lg font-semibold text-primary">What gets deleted</h2>
          </div>
          <ul className="space-y-1.5 ml-1 mb-3">
            {[
              'Your profile, display name, and avatar',
              'Your onboarding responses and readiness data',
              'Your salah logs and progress records',
              'Your direct messages',
              'Your notification history',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-text-main text-sm leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <Para>
            Posts you have made in the community are anonymized rather than deleted — they are attributed to a former member handle rather than your identity. If you would like your posts permanently deleted as well, include that request in your email and we will remove them within 30 days.
          </Para>
        </section>

        {/* Divider */}
        <div className="h-px mb-8" style={{ background: 'linear-gradient(to right, transparent, #4AADA820, #A99CC830, #4AADA820, transparent)' }} aria-hidden="true" />

        <Para>
          For any questions about your data or this process, you can also reach us at{' '}
          <a href="mailto:salam@sirati.co" className="text-primary hover:underline">salam@sirati.co</a>.
        </Para>
      </div>
    </div>
  );
};

export default DeleteDataPage;
